import {
  createContext, useContext, useEffect, useMemo, useState, useRef, type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { normalisePath, type PageData } from '@cng/shared';
import { fetchPageData } from '@/services/api';

interface PageDataState {
  data: PageData | null;
  loading: boolean;
  error: string | null;
  /** The path the current `data` describes; differs from the URL mid-navigation. */
  resolvedPath: string;
}

const PageDataContext = createContext<PageDataState>({
  data: null, loading: true, error: null, resolvedPath: '/',
});

declare global {
  interface Window {
    __CNG_DATA__?: PageData | null;
  }
}

export function PageDataProvider({
  initialData,
  children,
}: {
  initialData: PageData | null;
  children: ReactNode;
}) {
  const location = useLocation();
  const path = normalisePath(location.pathname);

  const [state, setState] = useState<PageDataState>(() => ({
    data: initialData,
    loading: !initialData,
    error: null,
    resolvedPath: initialData?.path ?? path,
  }));

  // Cache what we have already fetched so Back/Forward is instant.
  const cache = useRef(new Map<string, PageData>());
  if (initialData && !cache.current.has(initialData.path)) {
    cache.current.set(initialData.path, initialData);
  }

  useEffect(() => {
    if (state.resolvedPath === path && state.data) return;

    const cached = cache.current.get(path);
    if (cached) {
      setState({ data: cached, loading: false, error: null, resolvedPath: path });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchPageData(path)
      .then((data) => {
        if (cancelled) return;
        cache.current.set(path, data);
        setState({ data, loading: false, error: null, resolvedPath: path });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, error: err.message }));
      });

    return () => { cancelled = true; };
    // `state` is intentionally not a dependency: this effect reacts to navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const value = useMemo(() => state, [state]);
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>;
}

export function usePageData(): PageDataState {
  return useContext(PageDataContext);
}

/** Site-wide context (settings, menus, branches). Throws if used outside a loaded page. */
export function useSite() {
  const { data } = usePageData();
  if (!data) {
    throw new Error('useSite() was called before page data loaded — render inside <PageDataProvider>.');
  }
  return data.site;
}

export function useSettings() {
  return useSite().settings;
}

export function useBranches() {
  return useSite().branches;
}
